import { Fragment } from 'react';
import { AdminSpinner } from '../../../adminComponents/AdminSpinner/adminSpinner';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { formatDayLabel } from '../helpers/chatFormat';
import { MentionCandidate } from '../helpers/chatMentions';
import {
  CHAT_UNREAD_DIVIDER_ID,
  ChatViewer,
  isSameAuthorAs,
  isViktorFailure,
  OpenUser,
  startsNewDay,
} from '../helpers/chatMessage';
import { systemMessageText } from '../helpers/chatSystemMessage';
import { MessageTranslationState } from '../hooks/useMessageTranslations';
import { MessageBubble } from './MessageBubble';
import {
  ChatDivider,
  DividerSpinner,
  ErrorNote,
  SystemNote,
} from './MessageList.styled';

type Props = {
  messages: ConversationMessageWithStatus[];
  /** Who is reading, and what kind of conversation this is. */
  viewer: ChatViewer;
  fallbackSenderName?: string | null;
  canWrite?: boolean;
  /** Id of the first unread message, for the "New messages" marker. */
  firstUnreadId?: number | null;
  onReply?: (message: ConversationMessageWithStatus) => void;
  onEdit?: (message: ConversationMessageWithStatus) => void;
  onDelete?: (message: ConversationMessageWithStatus) => void;
  /** Held down on a touch screen - see MessageBubble. */
  onLongPress?: (
    message: ConversationMessageWithStatus,
    canModify: boolean
  ) => void;
  onRetry?: (message: ConversationMessageWithStatus) => void;
  onOpenOrder?: (orderId: number) => void;
  /** Opens a support ticket the triage bot raised. Admin only - see MessageBubble. */
  onOpenTicket?: (ticketId: number) => void;
  /** Jump from a reply strip to the message it quotes. */
  onOpenReply?: (messageId: number) => void;
  /** Opens a sender's detail screen from the name under their bubble. */
  onOpenUser?: OpenUser;
  /** Everyone in the conversation, so mentions in a body can be picked out. */
  members?: MentionCandidate[];
  /** A group thread, where `@all` in a body is a mention too. */
  mentionAll?: boolean;
  /** A message's translation, if the reader asked for one. */
  translationOf?: (
    message: ConversationMessageWithStatus
  ) => MessageTranslationState | null;
  /** Translate / See original - see MessageBubble. */
  onTranslate?: (message: ConversationMessageWithStatus) => void;
  /** Message currently playing the jump-target glare. */
  highlightMessageId?: number | null;
  /** Older history is being fetched; shown beside the topmost day pill. */
  loadingOlder?: boolean;
};

/**
 * Where each message sits relative to its neighbours: whether a divider breaks
 * above it, and whether it continues the run above or is continued below.
 *
 * A day divider or the unread marker breaks the run visually, so it has to
 * break the grouping too - otherwise the first message under a divider loses
 * its sender label.
 */
const placeInRun = (
  messages: ConversationMessageWithStatus[],
  index: number,
  firstUnreadId?: number | null
) => {
  const message = messages[index];
  const previous = messages[index - 1];
  const next = messages[index + 1];

  const newDay = startsNewDay(message, previous);
  const dividerAbove = newDay || firstUnreadId === message.id;
  const dividerBelow =
    !!next && (startsNewDay(next, message) || firstUnreadId === next.id);

  return {
    newDay,
    /* One run per author: everything they said in a row stacks tightly, with
       the name once at the end of it. */
    groupedWithPrevious: !dividerAbove && isSameAuthorAs(message, previous),
    groupedWithNext: !!next && !dividerBelow && isSameAuthorAs(next, message),
  };
};

/**
 * Messages are keyed by `clientMessageId`, never by `id`: an optimistic row
 * carries the same one the server echoes back, so the bubble keeps its
 * identity (and its DOM node) across the swap from pending to sent.
 */
export const MessageList = ({
  messages,
  viewer,
  fallbackSenderName,
  canWrite,
  firstUnreadId,
  onReply,
  onEdit,
  onDelete,
  onLongPress,
  onRetry,
  onOpenOrder,
  onOpenTicket,
  onOpenReply,
  onOpenUser,
  members,
  mentionAll,
  translationOf,
  onTranslate,
  highlightMessageId,
  loadingOlder,
}: Props) => (
  <>
    {messages.map((message, index) => {
      const run = placeInRun(messages, index, firstUnreadId);

      /*
       * Membership and metadata changes arrive as `side: 'system'` rows with
       * no body - the backend sends the event and its operands, the sentence
       * is ours to write. Anything we cannot phrase renders nothing rather
       * than printing a raw enum at someone.
       */
      const systemText =
        message.side === 'system'
          ? systemMessageText(message, viewer.id)
          : null;

      return (
        <Fragment key={message.clientMessageId || message.id}>
          {run.newDay && (
            <ChatDivider $first={index === 0}>
              {formatDayLabel(message.createdAt)}
              {index === 0 && loadingOlder && (
                <DividerSpinner>
                  <AdminSpinner absolute smallest $noBackground $noZIndex />
                </DividerSpinner>
              )}
            </ChatDivider>
          )}

          {firstUnreadId === message.id && (
            <ChatDivider $primary id={CHAT_UNREAD_DIVIDER_ID}>
              New messages
            </ChatDivider>
          )}

          {message.side === 'system' ? (
            !!systemText && <SystemNote>{systemText}</SystemNote>
          ) : isViktorFailure(message) ? (
            /* The backend's own sentence, as it came: `viktorStatus` alone
               decides this is an error, the text is never parsed. */
            <ErrorNote role="alert">{message.body}</ErrorNote>
          ) : (
            <MessageBubble
              message={message}
              viewer={viewer}
              fallbackSenderName={fallbackSenderName}
              canWrite={canWrite}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onLongPress={onLongPress}
              onRetry={onRetry}
              onOpenOrder={onOpenOrder}
              onOpenTicket={onOpenTicket}
              onOpenReply={onOpenReply}
              onOpenUser={onOpenUser}
              members={members}
              mentionAll={mentionAll}
              translation={translationOf?.(message)}
              onTranslate={onTranslate}
              highlighted={highlightMessageId === message.id}
              groupedWithPrevious={run.groupedWithPrevious}
              groupedWithNext={run.groupedWithNext}
            />
          )}
        </Fragment>
      );
    })}
  </>
);
